stzh-olmap {
  @include host;

  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;

  @media print {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  @include mq($from: small) {
    aspect-ratio: 16 / 9;
  }

  @include mq($from: medium) {
    aspect-ratio: 21 / 9;
  }
}

$olmapPadding: space("xsmall");

$olmapControlWidth: $formInputHeightSmall;
$olmapControlHeight: $formInputHeightSmall;
$olmapControlMargin: space("xsmall");

$olmapControlLayerThumbnailWidth: 76px;
$olmapControlLayerThumbnailHeight: 76px;
$olmapControlLayerThumbnailPadding: space("xxxsmall");

$olmapControlRotateWidth: 32px;
$olmapControlRotateHeight: 32px;

$olmapControlZoomGap: space("xxxsmall");

.stzh-olmap {
  &__map {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;

    &--print {
      margin-top: space("xxlarge");
      max-width: 100%;
      height: auto;
    }
  }

  &__print-image {
    display: block;
    max-width: 100%;
  }

  .ol-overlaycontainer-stopevent {
    touch-action: manipulation;
  }

  .ol-print,
  .ol-location,
  .ol-zoom-in,
  .ol-zoom-out,
  .ol-rotate-reset {
    @include font("light");
    @include fontSize("deca");
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    border: none;
    border-radius: 0;
    color: $colorPrimary;
    transition: all $baseTransitionAnimationSpeed ease;
    background-color: $colorWhite;
    width: $olmapControlWidth;
    height: $olmapControlHeight;
    cursor: pointer;

    stzh-icon {
      --size: #{iconSize("small")};
    }

    &:hover {
      color: $colorPrimary70;
      background: $colorSecondary20;
    }
  }

  .ol-layer,
  .ol-print,
  .ol-location,
  .ol-zoom,
  .ol-rotate-reset {
    position: absolute;
    right: $olmapPadding;
    bottom: $olmapPadding;
    box-shadow: $boxShadowPopover;
    border-radius: $buttonBorderRadius;

    @media print {
      display: none;
    }
  }

  .ol-layer {
    left: $olmapPadding;
    right: auto;
    padding: $olmapControlLayerThumbnailPadding;
    border: none;
    cursor: pointer;
  }

  .ol-layer__thumbnail {
    width: $olmapControlLayerThumbnailWidth;
    height: $olmapControlLayerThumbnailHeight;
    display: block;
  }

  .ol-layer__text {
    @include font("light");
    @include fontSize("pico");
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: left;
    background-color: $colorWhite;
    color: $colorPrimary;
    padding-left: calc(#{space("xxxsmall")} + 1px);
    padding-right: calc(#{space("xxxsmall")} + 1px);
  }

  .ol-layer:hover .ol-layer__text {
    color: $colorPrimaryHover;
  }

  .ol-location {
    margin-bottom: calc(
      #{$olmapControlHeight} * 3 + #{$olmapControlMargin} * 2
    );

    &:hover {
      color: $colorPrimaryHover;
      background: $colorWhite;
    }

    &.active {
      color: $colorWhite;
      background: $colorPrimary;
    }
  }

  .ol-zoom {
    margin-bottom: calc(
      #{$olmapControlHeight} + #{$olmapControlMargin} + #{$olmapControlZoomGap}
    );
    background-color: transparent;
    border-radius: $buttonBorderRadius;
    overflow: hidden;
  }

  .ol-zoom-in {
    margin-bottom: $olmapControlZoomGap;
  }

  .ol-rotate-reset {
    width: $olmapControlRotateWidth;
    height: $olmapControlRotateHeight;
    border-radius: 50%;
    box-shadow: none;
    margin-right: calc(
      (#{$olmapControlWidth} - #{$olmapControlRotateWidth}) / 2
    );
    margin-bottom: calc(
      #{$olmapControlHeight} * 3 + #{$olmapControlMargin} * 2 +
        #{$olmapControlZoomGap}
    );

    span {
      @include visuallyhidden;
    }

    svg {
      width: 15.84px;
      height: 21.15px;
    }
  }

  .ol-hidden {
    display: none;
  }

  .ol-compass {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ol-scale-line {
    position: absolute;
    left: $olmapPadding;
    bottom: $olmapPadding;
    margin-left: calc(
      #{$olmapControlLayerThumbnailWidth} +
        #{$olmapControlLayerThumbnailPadding} + #{$olmapControlMargin}
    );
  }

  .ol-scale-line-inner {
    @include fontSize("nano");
    position: relative;
    color: $colorBlack;
    text-align: center;
    filter: drop-shadow(-1px 0px 0px white) drop-shadow(1px 0px 0px white)
      drop-shadow(0px -1px 0px white) drop-shadow(0px 1px 0px white);

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4.5px;
      border: 1px solid $colorBlack;
      border-top: 0;
    }
  }

  .ol-attribution {
    display: none;
  }

  /* Has no layer control */

  &--has-no-layer-control .ol-scale-line {
    margin-left: 0;
  }

  /* Has no print control */

  &--has-no-print-control .ol-zoom {
    margin-bottom: 0;
  }

  &--has-no-print-control .ol-rotate-reset {
    margin-bottom: calc(
      #{$olmapControlHeight} * 2 + #{$olmapControlMargin} +
        #{$olmapControlZoomGap}
    );
  }

  /* Has location control */

  &--has-location-control .ol-rotate-reset {
    margin-bottom: calc(
      #{$olmapControlHeight} * 4 + #{$olmapControlMargin} * 3 +
        #{$olmapControlZoomGap}
    );
  }

  &--has-location-control#{&}--has-no-print-control .ol-location {
    margin-bottom: calc(
      #{$olmapControlHeight} * 2 + #{$olmapControlMargin} +
        #{$olmapControlZoomGap}
    );
  }

  &--has-location-control#{&}--has-no-print-control .ol-rotate-reset {
    margin-bottom: calc(
      #{$olmapControlHeight} * 3 + #{$olmapControlMargin} * 2 +
        #{$olmapControlZoomGap}
    );
  }

  /** Popover */
  &__popover {
    background-color: var(--stzh-color-grey10);
    width: 300px;

    @include mq($to: small) {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      z-index: 5;
      display: flex;
    }
  }

  &__popover-content {
    @include spaceCurve("padding", "small");

    stzh-heading {
      margin-bottom: space(xsmall);
    }

    @include mq($from: small) {
      aspect-ratio: 2 / 1;
    }
  }

  &__popover-link {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &__popover-image {
    position: relative;

    @include mq($to: small) {
      width: 140px;
      min-width: 140px;
      height: 140px;
      max-height: 140px;
      overflow: hidden;
    }
  }

  &__popover-image img {
    width: 100%;

    @include mq($to: small) {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transform: translate(-50%, -50%);
    }
  }

  &__popover * {
    text-decoration: none;
  }
}
